/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useRef } from 'react';

import {
  Control,
  FieldValues,
  Path,
  useController,
  UseControllerProps,
} from 'react-hook-form';

import classes from './DoubleInput.module.scss';
import { inputValidation } from './DoubleInput.setting';

type InputPropsTypes<T extends FieldValues, TName extends Path<T>> = {
  disabled?: boolean;
} & Pick<UseControllerProps<T, TName>, 'name' | 'rules' | 'defaultValue'>;

type DoubleInputProps<T extends FieldValues, TName extends Path<T>> = {
  control: Control<T>;
  firstInputProps: InputPropsTypes<T, TName>;
  secondInputProps: InputPropsTypes<T, TName>;
  label?: string;
  endAdornment?: string;
};
/**
 * use only in active calendar, need to refuctor (return set input width based on content) before use in other flows
 */
const DoubleInput = <T extends FieldValues, TName extends Path<T>>({
  control,
  firstInputProps,
  secondInputProps,
  label,
  endAdornment,
}: DoubleInputProps<T, TName>) => {
  const firstInputRef = useRef<HTMLInputElement | null>(null);

  const {
    field: fieldFirstInput,
    fieldState: { error: errorFirstInput },
  } = useController<any>({
    control,
    name: firstInputProps.name,
    rules: { ...inputValidation, ...firstInputProps.rules },
    defaultValue: firstInputProps.defaultValue || '',
  });

  const {
    field: fieldSecondInput,
    fieldState: { error: errorSecondInput },
  } = useController<any>({
    control,
    name: secondInputProps.name,
    rules: { ...inputValidation, ...secondInputProps.rules },
    defaultValue: secondInputProps.defaultValue || '',
  });

  const error = !!errorFirstInput || !!errorSecondInput;
  const errorMessage = errorFirstInput?.message || errorSecondInput?.message;

  return (
    <div className={classes.doubleInputBox} tabIndex={0} data-error={error}>
      {label && <div className={classes.label}>{label} </div>}
      <div className={classes.inputsRow} onClick={() => firstInputRef.current?.focus()}>
        <div className={classes.inputsContainer}>
          <input
            {...fieldFirstInput}
            ref={(el) => {
              firstInputRef.current = el;
              fieldFirstInput.ref(el);
            }}
            pattern="[0-9]*"
            placeholder="actual"
            onChange={(event) =>
              event.target.validity.valid && fieldFirstInput.onChange(event)
            }
          />
          <span> / </span>
          <input
            {...fieldSecondInput}
            pattern="[0-9]*"
            placeholder="in bottle"
            onClick={(event) => event.stopPropagation()}
            disabled={secondInputProps.disabled ?? true}
            onChange={(event) => {
              if (event.target.validity.valid) {
                fieldSecondInput.onChange(event);
              }
            }}
          />
        </div>
        <p className={classes.endAdornment}>{endAdornment}</p>
      </div>
      <span className={classes.errorMessage}>{errorMessage}</span>
    </div>
  );
};

export { DoubleInput };
